import React from 'react';
import { Box, Button, Typography } from '../../ui';
import { UploadIcon } from '../../icons';

/**
 * Background configuration section of the certificate editor. Shows a
 * remove-background banner when an uploaded background is in use and a
 * button that drives a hidden file input for uploading a new one.
 */
export default function CertificateBackgroundPanel({
  hasCustomBackground,
  backgroundInputRef,
  onUpload,
}: {
  hasCustomBackground: boolean;
  backgroundInputRef: React.RefObject<HTMLInputElement>;
  onUpload: (file: File | null) => void;
}) {
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 0.75 }}>
        BACKGROUND
      </Typography>
      {hasCustomBackground && (
        // Amber banner — surfaces that a background is set, with a
        // one-click removal so admins don't have to hunt for it. There's
        // no default artwork; removing just returns to a blank canvas.
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.25,
            py: 0.75,
            mb: 1,
            borderRadius: 1,
            bgcolor: 'warning.softBg',
            border: '1px solid #ffe082',
          }}
        >
          <Typography variant="caption" sx={{ color: 'warning.softText' }}>
            Using an uploaded background image.
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onUpload(null)}
            sx={{
              textTransform: 'none',
              py: 0.25,
              px: 1,
              fontSize: '0.75rem',
              borderColor: '#c89c00',
              color: 'warning.softText',
              '&:hover': { borderColor: 'warning.softText', bgcolor: 'warning.softBg' },
            }}
          >
            Remove
          </Button>
        </Box>
      )}
      {/* Replace-background CTA. Drives a hidden <input type=file>
          rather than rendering a drop zone — the drop zone took
          more vertical space than the action warrants, and admins
          only reach for it occasionally. The button reads as a
          clear single action while still using the same upload
          handler underneath. */}
      <Button
        variant="outlined"
        color="primary"
        startIcon={<UploadIcon sx={{ fontSize: 18 }} />}
        onClick={() => backgroundInputRef.current?.click()}
        fullWidth
        sx={{
          textTransform: 'none',
          fontWeight: 600,
          py: 1.25,
        }}
      >
        {hasCustomBackground ? 'Replace background image' : 'Upload background image'}
      </Button>
      <Box
        component="input"
        ref={backgroundInputRef}
        type="file"
        accept=".png,.jpg,.jpeg,.webp"
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const file = e.target.files?.[0] ?? null;
          onUpload(file);
          // Reset so picking the same file twice in a row still fires.
          e.target.value = '';
        }}
        sx={{ display: 'none' }}
      />
    </Box>
  );
}
